import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import {
  message,
  Card,
  Form,
  Input,
  Select,
  Radio,
  Button,
  Row,
  Col,
  Space,
  Typography,
  Tooltip,
  Alert,
  Checkbox,
  Tag,
  Divider,
  Steps,
  Progress,
} from "antd";
import {
  BellOutlined,
  SendOutlined,
  QuestionCircleOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  MessageOutlined,
  InfoCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
  CheckOutlined,
  RightOutlined,
  LeftOutlined,
  WarningOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  PauseCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { handleApiError } from "../../../utils/errorHandler";
import "./SuperAdminBroadcast.css";
import FooterSection from "../../shared/components/footerSection";
import superAdminService from "../../../services/super-admin/super-admin.service";
import { BroadcastRequest, Company } from "../../../types/super-admin.types";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Step } = Steps;

interface SuperAdminBroadcastForm {
  title: string;
  content: string;
  priority: "low" | "medium" | "high";
  types: {
    notification: boolean;
    email: boolean;
    sms: boolean;
  };
  companySelection: {
    type: "ALL" | "ACTIVE" | "INACTIVE";
    ids: number[];
  };
  targetRoles: string[];
}

const SuperAdminBroadcast = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [formData, setFormData] = useState<SuperAdminBroadcastForm>({
    title: "",
    content: "",
    priority: "medium",
    types: {
      notification: true,
      email: false,
      sms: false,
    },
    companySelection: {
      type: "ALL",
      ids: [],
    },
    targetRoles: ["ADMIN", "MANAGER", "USER"],
  });

  const steps = [
    {
      key: "methods",
      title: "Gönderim Yöntemi",
      icon: <SendOutlined />,
    },
    {
      key: "message",
      title: "Mesaj İçeriği",
      icon: <MessageOutlined />,
    },
    {
      key: "recipients",
      title: "Hedef Şirketler",
      icon: <GlobalOutlined />,
    },
    {
      key: "preview",
      title: "Önizleme",
      icon: <CheckOutlined />,
    },
  ];

  const fetchCompanies = useCallback(async () => {
    try {
      const companiesData = await superAdminService.getCompanies();
      setCompanies(companiesData);
    } catch (error) {
      handleApiError(error, t);
    }
  }, [t]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleSubmit = async (values: any) => {
    try {
      setIsSubmitting(true);

      // Hem values parametresini hem de form field'larını kontrol et
      const currentFormValues = form.getFieldsValue();

      // Title ve content'i her iki yerden de kontrol et (formData'dan öncelikli)
      const title =
        formData.title || values.title || currentFormValues.title || "";
      const content =
        formData.content || values.content || currentFormValues.content || "";
      const priority =
        formData.priority ||
        values.priority ||
        currentFormValues.priority ||
        "medium";


      // En az bir gönderim yöntemi seçilmiş olmalı
      if (!Object.values(formData.types).some(Boolean)) {
        message.error("En az bir gönderim yöntemi seçmelisiniz.");
        setIsSubmitting(false);
        return;
      }

      // Title ve content kontrolü
      if (!title?.trim() || !content?.trim()) {
        message.error("Başlık ve içerik alanları boş olamaz.");
        setIsSubmitting(false);
        return;
      }

      // Şirket seçimi kontrolü
      if (
        (formData.companySelection.type === "ACTIVE" ||
          formData.companySelection.type === "INACTIVE") &&
        formData.companySelection.ids.length === 0
      ) {
        message.error(
          "Belirli şirketler seçtiniz ancak hiç şirket seçmediniz."
        );
        setIsSubmitting(false);
        return;
      }

      // Kanal listesini oluştur
      const channels = [];
      if (formData.types.notification) channels.push("NOTIFICATION");
      if (formData.types.email) channels.push("EMAIL");
      if (formData.types.sms) channels.push("SMS");

      const broadcastData: BroadcastRequest = {
        title: title.trim(),
        content: content.trim(),
        priority: priority,
        types: formData.types,
        companySelection: formData.companySelection,
        targetRoles: formData.targetRoles || ["ADMIN", "MANAGER", "EMPLOYEE"],
        channels: channels,
      };

      // Debug için konsola yazdır

      const response = await superAdminService.sendBroadcast(broadcastData);

      message.success(
        `Toplu bildirim gönderildi! İşlem ID: ${response.broadcastId}`
      );
      setIsSubmitting(false);

      // Gönderdikten sonra ilk adıma geri dön ve formu sıfırla
      setCurrentStep(0);
      form.resetFields();
      setFormData({
        title: "",
        content: "",
        priority: "medium",
        types: {
          notification: true,
          email: false,
          sms: false,
        },
        companySelection: {
          type: "ALL",
          ids: [],
        },
        targetRoles: ["ADMIN", "MANAGER", "USER"],
      });
    } catch (error: any) {
      handleApiError(error, t);
      setIsSubmitting(false);
    }
  };

  const handleSendTypeChange = (type: "notification" | "email" | "sms") => {
    setFormData((prev) => ({
      ...prev,
      types: {
        ...prev.types,
        [type]: !prev.types[type],
      },
    }));
  };

  const renderTooltip = (text: string) => (
    <Tooltip title={text}>
      <QuestionCircleOutlined className="info-icon" />
    </Tooltip>
  );

  const nextStep = async () => {
    try {
      if (currentStep === 0) {
        // Gönderim yöntemi kontrolü
        if (!Object.values(formData.types).some(Boolean)) {
          message.error("En az bir gönderim yöntemi seçmelisiniz.");
          return;
        }
        setCurrentStep(1);
      } else if (currentStep === 1) {
        // Mesaj içerik bilgilerini kontrol et ve kaydet
        const values = await form.validateFields([
          "title",
          "content",
          "priority",
        ]);

        // Form verilerini formData'ya kaydet
        const updatedFormData = {
          ...formData,
          title: values.title,
          content: values.content,
          priority: values.priority,
        };

        setFormData(updatedFormData);



        setCurrentStep(2);
      } else if (currentStep === 2) {
        // Şirket seçimi kontrolü
        const values = await form.validateFields();
        if (
          (formData.companySelection.type === "ACTIVE" ||
            formData.companySelection.type === "INACTIVE") &&
          formData.companySelection.ids.length === 0
        ) {
          message.error("En az bir şirket seçmelisiniz.");
          return;
        }

        // Form verilerini güncelle
        const updatedFormData = {
          ...formData,
          title: values.title || formData.title,
          content: values.content || formData.content,
          priority: values.priority || formData.priority,
        };

        setFormData(updatedFormData);

        // Form değerlerini de güncelle (step 3'te görünmesi için)
        form.setFieldsValue({
          title: updatedFormData.title,
          content: updatedFormData.content,
          priority: updatedFormData.priority,
        });


        setCurrentStep(3);
      }
    } catch (error) {
    }
  };

  const renderSendMethodsContent = () => {
    const methods = [
      {
        key: "notification",
        icon: <BellOutlined />,
        text: "Bildirim",
        color: "#1890ff",
        description: "Uygulama içi push bildirimi",
      },
      {
        key: "email",
        icon: <MailOutlined />,
        text: "E-posta",
        color: "#52c41a",
        description: "E-posta ile bildirim",
      },
      {
        key: "sms",
        icon: <PhoneOutlined />,
        text: "SMS",
        color: "#fa8c16",
        description: "SMS ile bildirim",
      },
    ];

    return (
      <div className="send-methods">
        <Title level={5} className="send-methods-title">
          <Space>
            <SendOutlined />
            Gönderim Yöntemini Seçin
            {renderTooltip(
              "Bildiriminizi hangi kanallardan göndermek istediğinizi seçin."
            )}
          </Space>
        </Title>

        <Row gutter={[16, 16]}>
          {methods.map((method) => (
            <Col xs={24} sm={8} key={method.key}>
              <Card
                className={`send-method-card ${
                  formData.types[method.key as keyof typeof formData.types]
                    ? "active"
                    : ""
                }`}
                onClick={() => handleSendTypeChange(method.key as any)}
                hoverable
              >
                <div className="send-method-content">
                  <div
                    className="send-icon"
                    style={{ backgroundColor: `${method.color}30` }}
                  >
                    <span style={{ color: method.color }}>{method.icon}</span>
                  </div>
                  <div className="send-text">
                    <Text strong>{method.text}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                      {method.description}
                    </Text>
                  </div>
                  <Checkbox
                    checked={
                      formData.types[method.key as keyof typeof formData.types]
                    }
                    onChange={() => handleSendTypeChange(method.key as any)}
                  />
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  const renderMessageContent = () => {
    return (
      <div className="message-form">
        <Form.Item
          name="title"
          label={
            <Space>
              Başlık
              {renderTooltip(
                "Bildiriminizin başlığını girin. Bu, e-posta konusu ve bildirim başlığı olarak kullanılacaktır."
              )}
            </Space>
          }
          rules={[{ required: true, message: "Başlık zorunludur" }]}
        >
          <Input
            placeholder="Örn: Sistem Bakım Çalışması"
            prefix={<InfoCircleOutlined style={{ color: "#1890ff" }} />}
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="content"
          label={
            <Space>
              İçerik
              {renderTooltip("Bildiriminizin detaylı içeriğini girin.")}
            </Space>
          }
          rules={[{ required: true, message: "İçerik zorunludur" }]}
        >
          <TextArea
            placeholder="Bildirim içeriğinizi buraya yazın..."
            rows={6}
          />
        </Form.Item>

        <Form.Item
          name="priority"
          label={
            <Space>
              Öncelik Seviyesi
              {renderTooltip("Bildiriminizin önem derecesini belirleyin.")}
            </Space>
          }
          rules={[{ required: true, message: "Öncelik seviyesi seçmelisiniz" }]}
        >
          <Radio.Group className="priority-radio-group">
            <Row gutter={[16, 16]} className="priority-options">
              <Col xs={24} sm={8}>
                <Radio value="low">
                  <div className="priority-option low">
                    <div className="priority-badge">
                      <ArrowDownOutlined />
                    </div>
                    <div className="priority-info">
                      <Text strong>Düşük</Text>
                      <Progress
                        percent={33}
                        size="small"
                        strokeColor="#52c41a"
                        showInfo={false}
                      />
                      <Text type="secondary" className="priority-desc">
                        Genel bilgilendirme
                      </Text>
                    </div>
                  </div>
                </Radio>
              </Col>
              <Col xs={24} sm={8}>
                <Radio value="medium">
                  <div className="priority-option medium">
                    <div className="priority-badge">
                      <MinusOutlined />
                    </div>
                    <div className="priority-info">
                      <Text strong>Orta</Text>
                      <Progress
                        percent={66}
                        size="small"
                        strokeColor="#faad14"
                        showInfo={false}
                      />
                      <Text type="secondary" className="priority-desc">
                        Önemli duyuru
                      </Text>
                    </div>
                  </div>
                </Radio>
              </Col>
              <Col xs={24} sm={8}>
                <Radio value="high">
                  <div className="priority-option high">
                    <div className="priority-badge">
                      <ArrowUpOutlined />
                    </div>
                    <div className="priority-info">
                      <Text strong>Yüksek</Text>
                      <Progress
                        percent={100}
                        size="small"
                        strokeColor="#f5222d"
                        showInfo={false}
                      />
                      <Text type="secondary" className="priority-desc">
                        Acil bildirim
                      </Text>
                    </div>
                  </div>
                </Radio>
              </Col>
            </Row>
          </Radio.Group>
        </Form.Item>
      </div>
    );
  };

  const renderRecipientsContent = () => {
    return (
      <div className="recipients-form">
        <Form.Item
          name="companySelectionType"
          label={
            <Space>
              Hedef Şirketler
              {renderTooltip(
                "Bildiriminizi hangi şirketlere göndermek istediğinizi seçin."
              )}
            </Space>
          }
          rules={[{ required: true, message: "Şirket seçimi yapmalısınız" }]}
        >
          <Radio.Group
            value={formData.companySelection.type}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                companySelection: {
                  type: e.target.value,
                  ids:
                    e.target.value === "ALL" ? [] : prev.companySelection.ids,
                },
              }));
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} md={8}>
                <Radio.Button value="ALL" className="target-radio">
                  <Card className="target-card">
                    <div className="target-icon-wrapper">
                      <GlobalOutlined className="target-icon" />
                    </div>
                    <div className="target-info">
                      <Title level={5}>Tüm Şirketler</Title>
                      <Text type="secondary">
                        Sistemdeki tüm şirketlere gönder
                      </Text>
                    </div>
                  </Card>
                </Radio.Button>
              </Col>
              <Col xs={24} md={8}>
                <Radio.Button value="ACTIVE" className="target-radio">
                  <Card className="target-card">
                    <div className="target-icon-wrapper">
                      <CheckCircleOutlined className="target-icon" />
                    </div>
                    <div className="target-info">
                      <Title level={5}>Aktif Şirketler</Title>
                      <Text type="secondary">
                        Aktif durumda olan şirketleri seç
                      </Text>
                    </div>
                  </Card>
                </Radio.Button>
              </Col>
              <Col xs={24} md={8}>
                <Radio.Button value="INACTIVE" className="target-radio">
                  <Card className="target-card">
                    <div className="target-icon-wrapper">
                      <PauseCircleOutlined className="target-icon" />
                    </div>
                    <div className="target-info">
                      <Title level={5}>Pasif Şirketler</Title>
                      <Text type="secondary">
                        Pasif durumda olan şirketleri seç
                      </Text>
                    </div>
                  </Card>
                </Radio.Button>
              </Col>
            </Row>
          </Radio.Group>
        </Form.Item>

        {(formData.companySelection.type === "ACTIVE" ||
          formData.companySelection.type === "INACTIVE") && (
          <Form.Item
            label="Şirket Seçimi"
            rules={[
              { required: true, message: "En az bir şirket seçmelisiniz" },
            ]}
          >
            <Select
              mode="multiple"
              placeholder="Şirketleri seçin..."
              style={{ width: "100%" }}
              optionFilterProp="children"
              showSearch
              maxTagCount={2}
              maxTagPlaceholder={(omittedValues) =>
                `+${omittedValues.length} şirket daha`
              }
              value={formData.companySelection.ids}
              onChange={(values) => {
                setFormData((prev) => ({
                  ...prev,
                  companySelection: {
                    ...prev.companySelection,
                    ids: values,
                  },
                }));
              }}
            >
              {companies
                .filter((company) => {
                  if (formData.companySelection.type === "ACTIVE") {
                    return company.status !== "inactive";
                  }
                  if (formData.companySelection.type === "INACTIVE") {
                    return company.status === "inactive";
                  }
                  return true;
                })
                .map((company) => (
                  <Option key={company.id} value={company.id}>
                    <Space>
                      {company.name} - {company.email}
                      <Tag
                        color={company.status === "inactive" ? "red" : "green"}
                        style={{ fontSize: "10px" }}
                      >
                        {company.status === "inactive" ? "Pasif" : "Aktif"}
                      </Tag>
                    </Space>
                  </Option>
                ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="targetRoles"
          label={
            <Space>
              Hedef Roller
              {renderTooltip(
                "Bildirimini hangi kullanıcı rollerine göndermek istediğinizi seçin."
              )}
            </Space>
          }
        >
          <Checkbox.Group
            value={formData.targetRoles}
            onChange={(values) => {
              setFormData((prev) => ({
                ...prev,
                targetRoles: values as string[],
              }));
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Checkbox value="ADMIN">
                  <Tag color="red" icon={<UserOutlined />}>
                    Admin
                  </Tag>
                </Checkbox>
              </Col>
              <Col xs={24} sm={8}>
                <Checkbox value="MANAGER">
                  <Tag color="orange" icon={<UserOutlined />}>
                    Manager
                  </Tag>
                </Checkbox>
              </Col>
              <Col xs={24} sm={8}>
                <Checkbox value="USER">
                  <Tag color="blue" icon={<UserOutlined />}>
                    User
                  </Tag>
                </Checkbox>
              </Col>
            </Row>
          </Checkbox.Group>
        </Form.Item>
      </div>
    );
  };

  const renderPreview = () => {
    const values = form.getFieldsValue(true);
    const selectedCompanyNames =
      formData.companySelection.type === "ALL"
        ? ["Tüm Şirketler"]
        : companies
            .filter((company) =>
              formData.companySelection.ids.includes(company.id)
            )
            .map((company) => company.name);

    return (
      <div className="preview-content">
        {/* Hidden fields to preserve form data across steps */}
        <Form.Item name="title" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="content" hidden>
          <TextArea />
        </Form.Item>
        <Form.Item name="priority" hidden>
          <Input />
        </Form.Item>

        <div className="notification-preview">
          <div className="preview-header">
            <Space>
              <CheckOutlined />
              Gönderim Önizlemesi
            </Space>
          </div>

          <div className="preview-body">
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12}>
                <Card title="Mesaj Bilgileri" className="message-preview-card">
                  <div className="preview-item">
                    <Text strong>Başlık:</Text>
                    <Paragraph>
                      {formData.title || values.title || "Başlık belirtilmedi"}
                    </Paragraph>
                  </div>
                  <div className="preview-item">
                    <Text strong>İçerik:</Text>
                    <Paragraph>
                      {formData.content ||
                        values.content ||
                        "İçerik belirtilmedi"}
                    </Paragraph>
                  </div>
                  <div className="preview-item">
                    <Text strong>Öncelik:</Text>
                    <div className="priority-preview">
                      {(formData.priority || values.priority) === "low" && (
                        <div className="priority-option low">
                          <div className="priority-badge">
                            <ArrowDownOutlined />
                          </div>
                          <div className="priority-info">
                            <Text strong>Düşük</Text>
                            <Progress
                              percent={33}
                              size="small"
                              strokeColor="#52c41a"
                              showInfo={false}
                            />
                            <Text type="secondary" className="priority-desc">
                              Genel bilgilendirme
                            </Text>
                          </div>
                        </div>
                      )}
                      {(formData.priority || values.priority) === "medium" && (
                        <div className="priority-option medium">
                          <div className="priority-badge">
                            <MinusOutlined />
                          </div>
                          <div className="priority-info">
                            <Text strong>Orta</Text>
                            <Progress
                              percent={66}
                              size="small"
                              strokeColor="#faad14"
                              showInfo={false}
                            />
                            <Text type="secondary" className="priority-desc">
                              Önemli duyuru
                            </Text>
                          </div>
                        </div>
                      )}
                      {(formData.priority || values.priority) === "high" && (
                        <div className="priority-option high">
                          <div className="priority-badge">
                            <ArrowUpOutlined />
                          </div>
                          <div className="priority-info">
                            <Text strong>Yüksek</Text>
                            <Progress
                              percent={100}
                              size="small"
                              strokeColor="#f5222d"
                              showInfo={false}
                            />
                            <Text type="secondary" className="priority-desc">
                              Acil bildirim
                            </Text>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </Col>

              <Col xs={24} md={12}>
                <Card
                  title="Gönderim Bilgileri"
                  className="delivery-preview-card"
                >
                  <div className="preview-item">
                    <Text strong>Gönderim Yöntemleri:</Text>
                    <div className="method-tags">
                      {formData.types.notification && (
                        <Tag color="blue" icon={<BellOutlined />}>
                          Bildirim
                        </Tag>
                      )}
                      {formData.types.email && (
                        <Tag color="green" icon={<MailOutlined />}>
                          E-posta
                        </Tag>
                      )}
                      {formData.types.sms && (
                        <Tag color="orange" icon={<PhoneOutlined />}>
                          SMS
                        </Tag>
                      )}
                    </div>
                  </div>

                  <div className="preview-item">
                    <Text strong>Hedef Şirketler:</Text>
                    <div>
                      {selectedCompanyNames.map((name) => (
                        <Tag
                          key={name}
                          color="purple"
                          icon={<GlobalOutlined />}
                        >
                          {name}
                        </Tag>
                      ))}
                    </div>
                  </div>

                  <div className="preview-item">
                    <Text strong>Hedef Roller:</Text>
                    <div>
                      {formData.targetRoles.map((role) => (
                        <Tag key={role} color="cyan" icon={<UserOutlined />}>
                          {role}
                        </Tag>
                      ))}
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
          <Alert
            message="Gönderim Onayı"
            description="Bu bildirim seçilen şirketlerdeki tüm kullanıcılara gönderilecektir. Devam etmek istediğinizden emin misiniz?"
            type="info"
            showIcon
            icon={<WarningOutlined />}
            className="mt-4"
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Toplu Bildirim Gönder</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.superAdminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Süper Admin</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Toplu Bildirim
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          <Alert
            message="Toplu Bildirim Sistemi"
            description="Bu araçla seçtiğiniz şirketlere aynı anda bildirim, e-posta ve SMS gönderebilirsiniz. Lütfen gönderim öncesi içeriğinizi dikkatli kontrol edin."
            type="info"
            showIcon
            closable
            className="mb-4"
          />

          <Card className="notification-card">
            <Steps current={currentStep} className="notification-steps mb-4">
              {steps.map((item) => (
                <Step key={item.key} title={item.title} icon={item.icon} />
              ))}
            </Steps>

            <Divider />

            <Form
              form={form}
              layout="vertical"
              initialValues={{
                title: "",
                content: "",
                priority: "medium",
                companySelectionType: "ALL",
                targetRoles: ["ADMIN", "MANAGER", "USER"],
              }}
              onFinish={(values) => {
                if (currentStep === 3) {
                  handleSubmit(values);
                }
              }}
            >
              <div className="steps-content">
                {currentStep === 0 && renderSendMethodsContent()}
                {currentStep === 1 && renderMessageContent()}
                {currentStep === 2 && renderRecipientsContent()}
                {currentStep === 3 && renderPreview()}
              </div>

              <div className="steps-action mt-4">
                {currentStep > 0 && (
                  <Button
                    style={{ margin: "0 8px" }}
                    onClick={() => setCurrentStep(currentStep - 1)}
                    icon={<LeftOutlined />}
                    htmlType="button"
                  >
                    Önceki Adım
                  </Button>
                )}
                {currentStep < steps.length - 1 && (
                  <Button
                    type="primary"
                    onClick={nextStep}
                    icon={<RightOutlined />}
                    htmlType="button"
                  >
                    Sonraki Adım
                  </Button>
                )}
                {currentStep === steps.length - 1 && (
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    htmlType="submit"
                    loading={isSubmitting}
                    danger
                  >
                    Toplu Bildirim Gönder
                  </Button>
                )}
              </div>
            </Form>
          </Card>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <FooterSection />
        </div>
      </div>
    </>
  );
};

export default SuperAdminBroadcast;
